import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { ChefHat, WifiOff } from "lucide-react";
import EmptyState from "../../CommonComponents/EmptyState";
import PrimaryButton from "../../CommonComponents/PrimaryButton";
import SearchInput from "../../CommonComponents/SearchInput";
import Pagination from "../../CommonComponents/Pagination";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../../../utils/tableStyle";
import { authStorage } from "../../../utils/authStorage";
import { fetchAllRawMaterials } from "../../../services/posRawMaterialsApi";
import { createRawMaterialUsage, fetchRawMaterialUsageLogs } from "../../../services/rawMaterialUsageServices";

const formatQty = (qty) => {
  const num = Number(qty);
  if (!Number.isFinite(num)) return "0";
  return Number.isInteger(num) ? String(num) : num.toFixed(3).replace(/\.?0+$/, "");
};

const RawMaterialUsagePage = () => {
  const [tab, setTab] = useState("usage");
  const [materials, setMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [materialsError, setMaterialsError] = useState(null);
  const [rawMaterialId, setRawMaterialId] = useState("");
  const [qtyUsed, setQtyUsed] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const rowsPerPage = 10;

  const loadMaterials = useCallback(async () => {
    setMaterialsLoading(true);
    setMaterialsError(null);
    try {
      const user = authStorage.getUser();
      const branchId = user?.branch_id;
      const response = await fetchAllRawMaterials(branchId);
      const releasedOnly = (Array.isArray(response) ? response : []).filter((m) => Number(m.branch_stock) > 0);
      setMaterials(releasedOnly);
    } catch (err) {
      setMaterialsError(err);
    } finally {
      setMaterialsLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRawMaterialUsageLogs({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
      });
      setRows(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Math.max(Number(response?.pagination?.totalPages) || 1, 1));
      setTotalCount(Number(response?.pagination?.total) || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const materialOptions = useMemo(
    () =>
      materials.map((material) => ({
        value: String(material.id),
        label: `${material.name} (${formatQty(material.branch_stock)} ${material.unit_type || material.unit || ""})`,
      })),
    [materials]
  );

  const selectedMaterial = useMemo(
    () => materials.find((m) => String(m.id) === String(rawMaterialId)) || null,
    [materials, rawMaterialId]
  );

  const handleSubmitUsage = async (e) => {
    e.preventDefault();
    const qty = Number(qtyUsed);
    if (!rawMaterialId || !Number.isFinite(qty) || qty <= 0) {
      toast.error("Select a raw material and enter a valid quantity.");
      return;
    }
    const available = Number(selectedMaterial?.branch_stock) || 0;
    if (qty > available) {
      toast.error(`Insufficient released stock. Available: ${formatQty(available)}.`);
      return;
    }

    setSaving(true);
    try {
      await createRawMaterialUsage({
        raw_material_id: Number(rawMaterialId),
        qty_used: qty,
        reason: reason.trim(),
      });
      toast.success("Raw material usage recorded.");
      setRawMaterialId("");
      setQtyUsed("");
      setReason("");
      loadMaterials();
      loadLogs();
      setTab("logs");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record raw material usage.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="pt-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Raw Material Usage</h2>
        <p className="text-sm text-gray-500">Deduct branch released raw materials and track usage logs.</p>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "usage" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
          style={tab === "usage" ? { background: "var(--color-primary)" } : {}}
          onClick={() => setTab("usage")}
        >
          Usage
        </button>
        <button
          type="button"
          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "logs" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
          style={tab === "logs" ? { background: "var(--color-primary)" } : {}}
          onClick={() => setTab("logs")}
        >
          Usage Logs
        </button>
      </div>

      {tab === "usage" ? (
        materialsError ? (
          <EmptyState
            icon={WifiOff}
            title="Failed to Load Raw Materials"
            message="Check your connection and try again."
            onRetry={loadMaterials}
            retryLabel="Reload"
          />
        ) : materialsLoading ? (
          <div className="text-center py-8 text-gray-500">Loading branch raw materials...</div>
        ) : !materials.length ? (
          <EmptyState
            icon={ChefHat}
            title="No Released Raw Materials"
            message="Release raw materials to this branch first."
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <form onSubmit={handleSubmitUsage} className="space-y-4 max-w-xl">
              <div>
                <label className="text-sm font-medium text-gray-700">Raw Material</label>
                <div className="mt-1">
                  <Select
                    value={materialOptions.find((option) => option.value === String(rawMaterialId)) || null}
                    onChange={(selected) => setRawMaterialId(selected?.value || "")}
                    options={materialOptions}
                    placeholder="Search raw material..."
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        borderColor: "#e5e7eb",
                        minHeight: "40px",
                      }),
                      valueContainer: (base) => ({ ...base, padding: "0 12px" }),
                    }}
                  />
                </div>
                {selectedMaterial && (
                  <p className="mt-1 text-xs text-gray-500">
                    Released stock: {formatQty(selectedMaterial.branch_stock)} {selectedMaterial.unit_type || selectedMaterial.unit || ""}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Quantity Used</label>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={qtyUsed}
                  onChange={(e) => setQtyUsed(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Reason (Optional)</label>
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none"
                  placeholder="e.g. Dough preparation batch"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="pt-2">
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Record Usage"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        )
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Filter</h3>
            <div className="flex items-center gap-3 bg-[#f6f6f8] p-4 rounded-xl flex-wrap">
              <label className="text-sm font-semibold text-gray-700">Select Date Range:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
              {(startDate && endDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setAppliedStartDate(startDate);
                    setAppliedEndDate(endDate);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Submit
                </button>
              )}
              {(appliedStartDate || appliedEndDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setAppliedStartDate("");
                    setAppliedEndDate("");
                    setCurrentPage(1);
                  }}
                  className="text-sm font-semibold text-primary hover:text-primary-dark"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="mb-4">
            <SearchInput
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search usage logs by raw material..."
            />
          </div>

          {error ? (
            <EmptyState
              icon={WifiOff}
              title="Failed to Load Usage Logs"
              message="Check your connection and try again."
              onRetry={loadLogs}
              retryLabel="Reload"
            />
          ) : loading ? (
            <div className="text-center py-8 text-gray-500">Loading usage logs...</div>
          ) : !rows.length ? (
            <EmptyState
              icon={ChefHat}
              title="No Usage Logs"
              message="No raw material usage records found for your current filter."
            />
          ) : (
            <>
              <div className={tableContainer}>
                <table className={tableBase}>
                  <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                    <tr>
                      <th className={tableHeader + " text-center"}>Date</th>
                      <th className={tableHeader + " text-center"}>Time</th>
                      <th className={tableHeader + " text-center"}>Raw Material</th>
                      <th className={tableHeader + " text-center"}>Qty Used</th>
                      <th className={tableHeader + " text-center"}>Unit</th>
                      <th className={tableHeader + " text-center"}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className={tableRow + " border-b last:border-0"}>
                        <td className={tableCell + " text-center"}>{formatDate(row.created_at || row.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{formatTime(row.created_at || row.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{row.rawMaterial?.name || "-"}</td>
                        <td className={tableCell + " text-center tabular-nums"}>{formatQty(row.qty_used)}</td>
                        <td className={tableCell + " text-center"}>{row.rawMaterial?.unit_type || "-"}</td>
                        <td className={tableCell + " text-center"}>{row.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col items-center gap-2 mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
                <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
                  Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
                  {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RawMaterialUsagePage;
